import {
  GOOGLE_OAUTH_OUTCOMES,
  type GoogleOAuthKind,
  type GoogleOAuthOutcome,
  type IntegrationGroup,
  type IntegrationItemDto,
} from '@fos/shared/contracts/integrations';
import {
  GOOGLE_OAUTH_OUTCOME_MESSAGES,
  INTEGRATION_GROUP_LABELS,
  INTEGRATIONS_SIMULATION_BANNER,
} from '@fos/shared/labels';
import { Bell, Link2, MailPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { SettingsHeader } from '../pages/SettingsLayout';
import { IcsCalendarDialog } from './IcsCalendarDialog';
import { ConnectDialog } from './IntegrationDialogs';
import { IntegrationRow, IntegrationTile } from './IntegrationRow';
import { IntegrationSheet } from './IntegrationSheet';
import { GROUP_ORDER, newMailboxItem, toastMutationError } from './integrations-ui';

/**
 * Ustawienia → Integracje (Figma 12 `3035:4544`; MVP §9, §12; docs/INTEGRATIONS.md). Grupy „Firma · zarządza admin”
 * i „Twoje” (+ dla owner/admin konta osób z zespołu), wiersz na integrację, szczegóły i akcje w panelu bocznym.
 * Baner „System działa w trybie symulacji”, gdy żadna integracja zewnętrzna nie działa naprawdę. Konektory
 * podłączamy na końcu: „Połącz z Google” bez GOOGLE_CLIENT_ID jest wyłączone z notką. Kalendarz: „Skonfiguruj” =
 * okno „Kalendarz (link iCal)” (tajny adres iCal, Fala 9). Kolejną skrzynkę pocztową
 * (IMAP/SMTP, np. Zenbox; firmową albo osobistą) dodaje „Dodaj skrzynkę pocztową” w grupie firmowej. Powrót z Google:
 * `?google=<wynik>` → komunikat. Użytkownik bez roli managera: informacja zamiast listy (backend i tak odmawia).
 */

const PAGE_DESCRIPTION = 'Połączenia z pocztą, fakturami, kodem i AI. Hasła i klucze nie są widoczne w systemie.';

function useGoogleOutcomeToast() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const shown = useRef(false);
  const outcome = params.get('google');
  useEffect(() => {
    if (!outcome || shown.current) return;
    shown.current = true;
    if ((GOOGLE_OAUTH_OUTCOMES as readonly string[]).includes(outcome)) {
      const message = GOOGLE_OAUTH_OUTCOME_MESSAGES[outcome as GoogleOAuthOutcome];
      if (outcome === 'connected') toast.success(message);
      else toast.error(message);
    }
    navigate('/ustawienia/integracje', { replace: true });
  }, [outcome, navigate]);
}

export function IntegrationsSettingsPage() {
  const access = useAccess();
  useGoogleOutcomeToast();
  return (
    <div>
      <SettingsHeader title="Integracje" description={PAGE_DESCRIPTION} />
      {access.isManagerOrAbove ? (
        <IntegrationsList />
      ) : (
        <div className="mt-8">
          <EmptyState
            icon={Link2}
            title="Integracjami zarządza właściciel, admin albo manager"
            description="Poproś administratora, jeśli potrzebujesz połączenia z pocztą, fakturami albo AI."
          />
        </div>
      )}
    </div>
  );
}

/** Lista integracji (komponent do kompozycji, np. w innym widoku ustawień). */
export function IntegrationsList() {
  const list = trpc.integrations.list.useQuery();
  // panel i okno zostają zamontowane po zamknięciu (open = false): Radix przywraca wtedy fokus na przycisk wiersza
  const [sheet, setSheet] = useState<{ key: string; open: boolean } | null>(null);
  const [configure, setConfigure] = useState<{ key: string; open: boolean } | null>(null);
  const [addMailbox, setAddMailbox] = useState<{ open: boolean } | null>(null);
  const googleStart = trpc.integrations.googleStart.useMutation({
    onSuccess: (r) => window.location.assign(r.authorizationUrl),
    onError: toastMutationError,
  });

  if (list.isPending) {
    return (
      <Section className="mt-6">
        <ListSkeleton rows={6} />
      </Section>
    );
  }
  if (list.isError) {
    return (
      <Section className="mt-6">
        <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />
      </Section>
    );
  }
  const data = list.data;
  if (data.items.length === 0) {
    return (
      <Section className="mt-6">
        <EmptyState icon={Link2} title="Brak integracji" />
      </Section>
    );
  }

  const byGroup = new Map<IntegrationGroup, IntegrationItemDto[]>();
  for (const item of data.items) byGroup.set(item.group, [...(byGroup.get(item.group) ?? []), item]);
  const opened = sheet ? (data.items.find((i) => i.key === sheet.key) ?? null) : null;
  const configuring = configure ? (data.items.find((i) => i.key === configure.key) ?? null) : null;
  // kolejna skrzynka: gdy pierwsza już jest (karta „Niepodłączona” znika po pierwszej)
  const mailboxBase = data.items.find((i) => i.kind === 'gmail' && i.id !== null) ?? null;
  const newMailbox = mailboxBase ? newMailboxItem(mailboxBase) : null;
  const connectGoogle = (item: IntegrationItemDto) =>
    googleStart.mutate({
      kind: item.kind as GoogleOAuthKind,
      ...(item.id ? { integrationId: item.id } : {}),
    });

  return (
    <>
      {data.simulationOnly && (
        <ContextBanner tone="info" className="mt-6">
          {INTEGRATIONS_SIMULATION_BANNER}
        </ContextBanner>
      )}
      {GROUP_ORDER.filter((g) => byGroup.has(g) || g === 'mine').map((group) => (
        <Section key={group} className="mt-6" aria-labelledby={`integrations-${group}`}>
          <SectionHeader
            tone="strong"
            title={<span id={`integrations-${group}`}>{INTEGRATION_GROUP_LABELS[group]}</span>}
          />
          <div>
            {(byGroup.get(group) ?? []).map((item) => (
              <IntegrationRow
                key={item.key}
                item={item}
                onOpen={(i) => setSheet({ key: i.key, open: true })}
                onConfigure={(i) => setConfigure({ key: i.key, open: true })}
              />
            ))}
            {group === 'company' && newMailbox && (
              <IntegrationTile
                icon={MailPlus}
                title="Dodaj skrzynkę pocztową"
                description="IMAP/SMTP, np. Zenbox — firmowa albo osobista"
                status={null}
                action={
                  <Button
                    variant="secondary"
                    size="s"
                    className={TOUCH_BUTTON}
                    onClick={() => setAddMailbox({ open: true })}
                  >
                    Dodaj
                  </Button>
                }
              />
            )}
            {group === 'mine' && (
              <IntegrationTile
                icon={Bell}
                title="Powiadomienia na telefon"
                description="Pilne alarmy i akceptacje (Web Push)"
                status={null}
                action={
                  <Button variant="ghost" size="s" className={TOUCH_BUTTON} asChild>
                    <Link to="/ustawienia/powiadomienia" aria-label="Ustawienia: Powiadomienia na telefon">
                      Ustawienia
                    </Link>
                  </Button>
                }
              />
            )}
          </div>
        </Section>
      ))}

      {opened && sheet && (
        <IntegrationSheet
          open={sheet.open}
          onOpenChange={(open) => setSheet({ key: sheet.key, open })}
          item={opened}
          googleAvailable={data.googleOAuth.available}
          googlePending={googleStart.isPending}
          onGoogleConnect={connectGoogle}
        />
      )}
      {configuring && configure && configuring.kind === 'google_calendar' && (
        <IcsCalendarDialog
          open={configure.open}
          onOpenChange={(open) => setConfigure({ key: configure.key, open })}
          item={configuring}
        />
      )}
      {configuring && configure && configuring.kind !== 'google_calendar' && (
        <ConnectDialog
          open={configure.open}
          onOpenChange={(open) => setConfigure({ key: configure.key, open })}
          item={configuring}
        />
      )}
      {newMailbox && addMailbox && (
        <ConnectDialog open={addMailbox.open} onOpenChange={(open) => setAddMailbox({ open })} item={newMailbox} />
      )}
    </>
  );
}
